import { useState, useEffect, useRef, useCallback } from "react";
import * as ort from "onnxruntime-web";
import { getPreloadedSession, preloadYoloModel } from "../utils/yoloModelCache";
import { createCanvasPool } from "../utils/canvasPool";
import { trackEvent } from "../utils/telemetry";

// Funnel-event dedup, keyed per event+side and held at module scope so it
// survives remounts (the capture screen remounts per side/retake), matching
// the page-load semantics of trackEvent's `once`. trackEvent's own `once`
// can't be used here — it dedupes on name alone and would swallow the
// back-side milestones.
const emittedFunnelKeys = new Set();
function trackFunnelOncePerSide(name, props = {}) {
  const key = `${name}:${props.side || ""}`;
  if (emittedFunnelKeys.has(key)) return;
  emittedFunnelKeys.add(key);
  trackEvent(name, props);
}

const THROTTLE_MS = 150;
const MODEL_URL = "/models/document-detect/yolov8n-doc.onnx";

// The capture mode ("snap" | "steady" | "precise" | "stupid" | "assisted") stamped
// onto telemetry. Exported so the capture screen and flow pages report
// the same resolved value the hook actually runs with.
export const resolveCaptureMode = (captureSettings) =>
  captureSettings?.captureStrategy || DEFAULT_STRATEGY;
const INPUT_SIZE = 640;
const CONF_THRESHOLD = 0.12;
const IOU_THRESHOLD = 0.45;

// ── Fixed thresholds (not strategy-dependent) ──
const DOC_SIZE_MAX = 0.80;
const GLARE_PIXEL_THRESHOLD = 240;
// A pixel this bright is clipped by the sensor: a specular highlight, not
// paper. White card stock under normal exposure sits around 200-245, so
// the "near white" count above reads the back of many IDs (mostly white)
// as glare. The back-side gate uses this clipped count instead.
const GLARE_CLIPPED_THRESHOLD = 250;
const LUMA_MEAN_MIN = 30;
const LUMA_MEAN_MAX = 230;
const LUMA_STD_MIN = 10;
const DARK_ROOM_THRESHOLD = 30;
const MANUAL_CAPTURE_TIMEOUT = 10000;
// Best-frame window (assisted mode). A frame that passed the quality
// gates is kept as the shutter's candidate for this long; older bests
// are replaced by the next passing frame even if it scores lower, so
// the shutter never uploads what the user was framing seconds ago.
// Coarse platform for per-OS capture defaults: the two phone platforms
// produce very different frames (Android front blur median ~600 vs
// ~2,400 on iOS at the same 1080x1920), so one sharpness floor cannot
// serve both. Exported so the flow container resolves overrides the
// same way and telemetry can split on it.
export function captureOs() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  if (/Android/i.test(ua)) return "android";
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1)) return "ios";
  return "other";
}
const CAPTURE_OS = captureOs();
const BEST_FRAME_WINDOW_MS = CAPTURE_OS === "android" ? 3000 : 2000;
// Android is 3s: its autofocus hunts for longer after the card settles,
// so the sharpest frame is further back than on iOS. First day of the v2
// rollout the best frame replaced the tap on 47% of Android shots
// against 93% on iOS, with the best only ~20% sharper than the tap.
// Luma band where a doc frame reads well; outside it sharpness is
// discounted rather than excluded (mirrors the face best-frame scoring).
const BEST_FRAME_LUMA_MIN = 70;
const BEST_FRAME_LUMA_MAX = 200;

// ── Capture strategy presets ──
// "snap"    — grab and go: minimal hold time, lenient quality gates
// "steady"  — balanced default: quick but reliable
// "precise" — thorough: strict quality, longer confirmation hold
const CAPTURE_STRATEGIES = {
  snap: {
    autoCaptureDelay: 0,          // capture immediately when gates pass
    temporalFrames: 3,            // require 3 stable frames (~450ms) to confirm front detection
    temporalFramesBack: 3,        // back side: require 3 stable frames (~450ms) for focus
    temporalIOU: 0.35,            // loose stability requirement
    blurThreshold: 25,            // very lenient on blur
    glareAreaThreshold: 0.15,     // tolerate more glare
    centerThreshold: 0.25,        // loose centering
    minimumDocumentArea: 0.08,    // accept smaller docs in frame
    minQualityGates: 3,           // need 3/5 quality gates (YOLO gates always required)
    qualityAnalysis: "skip",      // skip blur/glare/lighting per-frame analysis
  },
  stupid: {
    skipModels: true,             // don't load any models at all
    autoCaptureDelay: Infinity,   // never auto-capture
    temporalFrames: 0,
    temporalFramesBack: 0,
    temporalIOU: 0,
    blurThreshold: 0,
    glareAreaThreshold: 1,
    centerThreshold: 1,
    minimumDocumentArea: 0,
    minQualityGates: 0,
    qualityAnalysis: "skip",
  },
  // "assisted" — manual capture with light image-quality gates. No
  // models: the frame loop still runs, but only blur / glare / lighting
  // are measured inside the guide box (the on-screen frame when the
  // capture screen registers one via setGuideBoxProvider, else a 70%
  // centered box). Never auto-captures; the capture screen decides what
  // to do with the gates — capture-v2 holds the shutter until they pass.
  assisted: {
    skipModels: true,
    runFrameLoop: true,
    autoCaptureDelay: Infinity,
    temporalFrames: 0,
    temporalFramesBack: 0,
    temporalIOU: 0,
    // Per-OS sharpness floor. Fronts over 600 verify at 68-70%, under 300 at
    // ~40% (v2, n=340). iOS clears 400 easily (median ~2,400) so the floor
    // catches only its bad shots; Android sits at ~600 median with half its
    // frames under 400, so a higher floor there just holds users to the cap.
    blurThreshold: CAPTURE_OS === "ios" ? 400 : 250,
                                  // the 7s hold cap still releases the shutter, this just asks for focus first
    glareAreaThreshold: 0.02,     // fronts with >2% near-white pixels verified at 25-43% capture-fail (v2 day one)
    // Back side: bright (mostly white) backs tripped the 2% near-white gate
    // 24% of the time against 5% for other cards, and tilting cannot fix
    // paper being white. The back gates on clipped pixels (> 250) instead,
    // same 2% ceiling unless glareAreaThresholdBack overrides it.
    glareBackMetric: "clipped",
    lumaStdMin: 30,               // contrast floor: under 30 verified at 22% capture-fail vs 11% above
    motionThreshold: 10,          // max frame-to-frame CHANGE in the motion reading; the reading's level is
                                  // the sensor's noise floor (17-23 on an iPhone at rest), a moving hand swings it
    presenceEdgeMin: 8,           // mean gradient magnitude inside the guide (0-255) below which nothing is there
    presenceRatioMin: 1.2,        // inside-guide edge energy must beat the ring just outside it by this factor
                                  // (a card that overfills the guide spills edges into the ring; start permissive
                                  // and tune from edge_in / edge_out on id_doc_captured and the cancel events)
    centerThreshold: 1,
    minimumDocumentArea: 0,
    minQualityGates: 0,
    qualityAnalysis: "full",
  },
  steady: {
    autoCaptureDelay: 700,        // short hold — feels responsive
    temporalFrames: 2,            // two consecutive frames
    temporalFramesBack: 1,        // back side: single frame
    temporalIOU: 0.45,            // moderate stability
    blurThreshold: 50,            // moderate blur tolerance
    glareAreaThreshold: 0.10,     // moderate glare tolerance
    centerThreshold: 0.20,        // moderate centering
    minimumDocumentArea: 0.10,    // moderate size requirement
  },
  precise: {
    autoCaptureDelay: 1500,       // hold still for 1.5s
    temporalFrames: 3,            // three consecutive stable frames
    temporalFramesBack: 2,        // back side: two frames
    temporalIOU: 0.60,            // strict stability
    blurThreshold: 100,           // strict blur check
    glareAreaThreshold: 0.05,     // strict glare check
    centerThreshold: 0.15,        // strict centering
    minimumDocumentArea: 0.15,    // document must fill more of frame
  },
};

const DEFAULT_STRATEGY = "snap";

const CLASS_NAMES = ["passport", "id_card", "drivers_license", "id_card_back", "drivers_license_back"];

// Classes 3 and 4 are back-of-document detections
const BACK_CLASS_IDS = new Set([3, 4]);

const defaultGatingState = {
  documentPresent: false,
  classified: false,
  size: false,
  centering: false,
  blur: false,
  glare: false,
  lighting: false,
  // Assisted mode only: consecutive-frame motion inside the guide box.
  // Stays true for strategies that don't measure it.
  steady: true,
  // Assisted mode only: edge-density heuristic that says something
  // card-like fills the guide (vs a bare table). Gates auto-capture,
  // never the shutter. Stays true for strategies that don't measure it.
  documentLikely: true,
};

// ── Image analysis (pool-aware) ──

function analyzeLighting(video, box, pool) {
  const x = Math.max(0, Math.round(box.x));
  const y = Math.max(0, Math.round(box.y));
  const w = Math.min(Math.round(box.width), video.videoWidth - x);
  const h = Math.min(Math.round(box.height), video.videoHeight - y);
  if (w <= 0 || h <= 0) return { mean: 0, stdDev: 0 };

  const entry = pool.acquire(w, h);
  entry.ctx.drawImage(video, x, y, w, h, 0, 0, w, h);
  const imageData = entry.ctx.getImageData(0, 0, w, h);
  pool.release(entry);
  const data = imageData.data;

  let sum = 0;
  let count = 0;
  const lumaValues = [];
  for (let i = 0; i < data.length; i += 4) {
    const luma = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    lumaValues.push(luma);
    sum += luma;
    count++;
  }

  const mean = count > 0 ? sum / count : 0;
  let variance = 0;
  for (let i = 0; i < lumaValues.length; i++) {
    variance += (lumaValues[i] - mean) ** 2;
  }
  const stdDev = count > 0 ? Math.sqrt(variance / count) : 0;

  return { mean, stdDev };
}

function analyzeBlur(video, box, pool) {
  const x = Math.max(0, Math.round(box.x));
  const y = Math.max(0, Math.round(box.y));
  const w = Math.min(Math.round(box.width), video.videoWidth - x);
  const h = Math.min(Math.round(box.height), video.videoHeight - y);
  if (w <= 0 || h <= 0) return 0;

  const sw = Math.min(w, 200);
  const sh = Math.min(h, 150);
  const entry = pool.acquire(sw, sh);
  entry.ctx.drawImage(video, x, y, w, h, 0, 0, sw, sh);
  const imageData = entry.ctx.getImageData(0, 0, sw, sh);
  pool.release(entry);
  const data = imageData.data;

  const gray = new Float32Array(sw * sh);
  for (let i = 0; i < gray.length; i++) {
    const pi = i * 4;
    gray[i] = 0.299 * data[pi] + 0.587 * data[pi + 1] + 0.114 * data[pi + 2];
  }

  let lapSum = 0;
  let lapSumSq = 0;
  let lapCount = 0;
  for (let row = 1; row < sh - 1; row++) {
    for (let col = 1; col < sw - 1; col++) {
      const idx = row * sw + col;
      const lap =
        gray[idx - sw] +
        gray[idx - 1] +
        -4 * gray[idx] +
        gray[idx + 1] +
        gray[idx + sw];
      lapSum += lap;
      lapSumSq += lap * lap;
      lapCount++;
    }
  }

  if (lapCount === 0) return 0;
  const lapMean = lapSum / lapCount;
  const lapVariance = lapSumSq / lapCount - lapMean * lapMean;
  return lapVariance;
}

function analyzeGlare(video, box, pool) {
  const x = Math.max(0, Math.round(box.x));
  const y = Math.max(0, Math.round(box.y));
  const w = Math.min(Math.round(box.width), video.videoWidth - x);
  const h = Math.min(Math.round(box.height), video.videoHeight - y);
  if (w <= 0 || h <= 0) return { ratio: 0, clipped: 0 };

  const sw = Math.min(w, 200);
  const sh = Math.min(h, 150);
  const entry = pool.acquire(sw, sh);
  entry.ctx.drawImage(video, x, y, w, h, 0, 0, sw, sh);
  const imageData = entry.ctx.getImageData(0, 0, sw, sh);
  pool.release(entry);
  const data = imageData.data;

  let brightPixels = 0;
  let clippedPixels = 0;
  const totalPixels = sw * sh;
  for (let i = 0; i < data.length; i += 4) {
    const luma = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    if (luma > GLARE_PIXEL_THRESHOLD) brightPixels++;
    if (luma > GLARE_CLIPPED_THRESHOLD) clippedPixels++;
  }

  return totalPixels > 0
    ? { ratio: brightPixels / totalPixels, clipped: clippedPixels / totalPixels }
    : { ratio: 0, clipped: 0 };
}

// Motion between this frame and the previous one inside the box: mean
// absolute luma difference on a coarse downsample (0-255). Camera shake
// and a hand still settling read high; a phone resting over the card
// reads near zero. Cheap enough to run every frame; the previous
// frame's gray plane lives in `state` and is replaced on every call.
function analyzeMotion(video, box, pool, state) {
  const x = Math.max(0, Math.round(box.x));
  const y = Math.max(0, Math.round(box.y));
  const w = Math.min(Math.round(box.width), video.videoWidth - x);
  const h = Math.min(Math.round(box.height), video.videoHeight - y);
  if (w <= 0 || h <= 0) return null;

  const sw = 96;
  const sh = 60;
  const entry = pool.acquire(sw, sh);
  entry.ctx.drawImage(video, x, y, w, h, 0, 0, sw, sh);
  const data = entry.ctx.getImageData(0, 0, sw, sh).data;
  pool.release(entry);

  const gray = new Float32Array(sw * sh);
  for (let i = 0; i < gray.length; i++) {
    const pi = i * 4;
    gray[i] = 0.299 * data[pi] + 0.587 * data[pi + 1] + 0.114 * data[pi + 2];
  }
  const prev = state.prev;
  state.prev = gray;
  if (!prev || prev.length !== gray.length) {
    state.prevMotion = null;
    return null;
  }
  let sum = 0;
  for (let i = 0; i < gray.length; i++) sum += Math.abs(gray[i] - prev[i]);
  const motion = sum / gray.length;
  // The absolute reading never settles near zero on a real sensor: noise,
  // auto-exposure and sub-pixel drift on a high-contrast card give a
  // floor that varies by device (17-23 on an iPhone at rest, 78+ while
  // moving). What does distinguish a resting hand is that the reading
  // stops changing frame to frame, so the gate looks at the delta.
  const prevMotion = state.prevMotion;
  state.prevMotion = motion;
  const delta = prevMotion == null ? null : Math.abs(motion - prevMotion);
  return { motion, delta };
}

// Document presence, without a model: edge energy (mean gradient
// magnitude) inside the guide box against a ring just outside it. A card
// with text and a photo has far more structure than the table it lies
// on; a bare table reads about the same in both regions. Returns null
// when the guide fills the frame (no ring to compare against).
function analyzePresence(video, box, pool) {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  const bx = Math.max(0, Math.round(box.x));
  const by = Math.max(0, Math.round(box.y));
  const bw = Math.min(Math.round(box.width), vw - bx);
  const bh = Math.min(Math.round(box.height), vh - by);
  if (bw <= 0 || bh <= 0) return null;
  const pad = Math.round(Math.min(bw, bh) * 0.25);
  const ox = Math.max(0, bx - pad);
  const oy = Math.max(0, by - pad);
  const ow = Math.min(vw - ox, bx + bw + pad - ox);
  const oh = Math.min(vh - oy, by + bh + pad - oy);
  if (ow <= bw + 4 && oh <= bh + 4) return null;

  const sw = 128;
  const sh = 80;
  const entry = pool.acquire(sw, sh);
  entry.ctx.drawImage(video, ox, oy, ow, oh, 0, 0, sw, sh);
  const data = entry.ctx.getImageData(0, 0, sw, sh).data;
  pool.release(entry);

  const gray = new Float32Array(sw * sh);
  for (let i = 0; i < gray.length; i++) {
    const pi = i * 4;
    gray[i] = 0.299 * data[pi] + 0.587 * data[pi + 1] + 0.114 * data[pi + 2];
  }
  // Inner box in the downscaled frame.
  const sx = (sw / ow);
  const sy = (sh / oh);
  const ix0 = Math.round((bx - ox) * sx);
  const iy0 = Math.round((by - oy) * sy);
  const ix1 = Math.round((bx + bw - ox) * sx);
  const iy1 = Math.round((by + bh - oy) * sy);
  let inSum = 0, inN = 0, outSum = 0, outN = 0;
  for (let y = 1; y < sh - 1; y++) {
    for (let x = 1; x < sw - 1; x++) {
      const i = y * sw + x;
      const g = Math.abs(gray[i + 1] - gray[i - 1]) + Math.abs(gray[i + sw] - gray[i - sw]);
      if (x >= ix0 && x < ix1 && y >= iy0 && y < iy1) { inSum += g; inN++; } else { outSum += g; outN++; }
    }
  }
  if (inN === 0 || outN === 0) return null;
  return { edge_in: inSum / inN, edge_out: outSum / outN };
}

function analyzeFrameBrightness(video, pool) {
  const sw = 64;
  const sh = 48;
  const entry = pool.acquire(sw, sh);
  entry.ctx.drawImage(video, 0, 0, sw, sh);
  const imageData = entry.ctx.getImageData(0, 0, sw, sh);
  pool.release(entry);
  const data = imageData.data;
  let sum = 0;
  let count = 0;
  for (let i = 0; i < data.length; i += 4) {
    sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    count++;
  }
  return count > 0 ? sum / count : 0;
}

// ── YOLO preprocessing ──

function preprocessFrame(video, canvas, ctx) {
  canvas.width = INPUT_SIZE;
  canvas.height = INPUT_SIZE;
  ctx.drawImage(video, 0, 0, INPUT_SIZE, INPUT_SIZE);
  const imageData = ctx.getImageData(0, 0, INPUT_SIZE, INPUT_SIZE);
  const data = imageData.data;
  const float32Data = new Float32Array(3 * INPUT_SIZE * INPUT_SIZE);
  const pixelCount = INPUT_SIZE * INPUT_SIZE;

  // NHWC → NCHW, normalize to 0-1
  for (let i = 0; i < pixelCount; i++) {
    const pi = i * 4;
    float32Data[i] = data[pi] / 255.0;                          // R channel
    float32Data[pixelCount + i] = data[pi + 1] / 255.0;         // G channel
    float32Data[2 * pixelCount + i] = data[pi + 2] / 255.0;     // B channel
  }

  return new ort.Tensor("float32", float32Data, [1, 3, INPUT_SIZE, INPUT_SIZE]);
}

// ── YOLO postprocessing ──

function iou(a, b) {
  const x1 = Math.max(a.x, b.x);
  const y1 = Math.max(a.y, b.y);
  const x2 = Math.min(a.x + a.width, b.x + b.width);
  const y2 = Math.min(a.y + a.height, b.y + b.height);
  const inter = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
  const union = a.width * a.height + b.width * b.height - inter;
  return union > 0 ? inter / union : 0;
}

function postprocessYolo(output, videoWidth, videoHeight) {
  const data = output.data;
  const [, numFields, numDetections] = output.dims; // [1, 4+numClasses, 8400]
  const numClasses = numFields - 4;

  const scaleX = videoWidth / INPUT_SIZE;
  const scaleY = videoHeight / INPUT_SIZE;
  const candidates = [];

  // Track peak score per class across ALL 8400 candidates (for debug)
  const peakScores = new Array(numClasses).fill(0);

  for (let i = 0; i < numDetections; i++) {
    const cx = data[0 * numDetections + i];
    const cy = data[1 * numDetections + i];
    const w = data[2 * numDetections + i];
    const h = data[3 * numDetections + i];

    let maxScore = 0;
    let classId = -1;
    for (let c = 0; c < numClasses; c++) {
      const score = data[(4 + c) * numDetections + i];
      if (score > peakScores[c]) peakScores[c] = score;
      if (score > maxScore) {
        maxScore = score;
        classId = c;
      }
    }

    if (maxScore >= CONF_THRESHOLD) {
      candidates.push({
        x: (cx - w / 2) * scaleX,
        y: (cy - h / 2) * scaleY,
        width: w * scaleX,
        height: h * scaleY,
        classId,
        className: CLASS_NAMES[classId] || "unknown",
        confidence: maxScore,
      });
    }
  }

  candidates.sort((a, b) => b.confidence - a.confidence);
  const kept = [];
  const suppressed = new Set();

  for (let i = 0; i < candidates.length; i++) {
    if (suppressed.has(i)) continue;
    kept.push(candidates[i]);
    for (let j = i + 1; j < candidates.length; j++) {
      if (!suppressed.has(j) && iou(candidates[i], candidates[j]) > IOU_THRESHOLD) {
        suppressed.add(j);
      }
    }
  }

  kept.peakScores = peakScores.map((s, i) => ({ cls: CLASS_NAMES[i] || `c${i}`, peak: s }));
  return kept;
}

// ── Feedback mapping ──

function getFeedbackKey(gating, isTooDark, areaRatio, minArea, maxArea) {
  if (!gating.documentPresent) return isTooDark ? "tooDark" : "noDocument";
  if (!gating.size) {
    if (areaRatio < minArea) return "moveCloser";
    if (areaRatio > maxArea) return "moveFurther";
    return "moveCloser";
  }
  if (!gating.centering) return "centerDocument";
  if (!gating.blur) return "tooBlurry";
  if (!gating.glare) return "reduceGlare";
  if (!gating.lighting) return isTooDark ? "tooDark" : "tooBright";
  return "holdStill";
}

// ── Main hook ──

export default function useDocumentDetection(videoRef, active, captureSettings = {}, side = "front") {
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [modelLoadError, setModelLoadError] = useState(null);
  const [gatingState, setGatingState] = useState(defaultGatingState);
  const [feedbackKey, setFeedbackKey] = useState("positionFront");
  const [isAllPassing, setIsAllPassing] = useState(false);
  const [isTooDark, setIsTooDark] = useState(false);
  const [documentType, setDocumentType] = useState(null);
  const [confidence, setConfidence] = useState(0);
  const [boundingBox, setBoundingBox] = useState(null);
  const [showManualCapture, setShowManualCapture] = useState(false);
  const [yoloDetail, setYoloDetail] = useState(null);
  const [captured, setCaptured] = useState(false);

  const sessionRef = useRef(null);
  const sessionFromCacheRef = useRef(false);
  const rafRef = useRef(null);
  const timerRef = useRef(null);
  // While set, the frame loop idles (polls every 100ms, analyses nothing)
  // without tearing down or resetting any state. The capture screen sets
  // it for the span of a shutter press: on Android the per-frame quality
  // analysis saturates the main thread (the last analysed frame is a
  // median 0.7s old at capture time, 2.5s at p90), and the canvas.toBlob
  // completion task sat behind it for seconds while taps kept arriving.
  const loopSuspendedRef = useRef(false);
  const lastDetectRef = useRef(0);
  const onAutoCaptureRef = useRef(null);
  const preprocessCanvasRef = useRef(null);
  const preprocessCtxRef = useRef(null);
  const areaRatioRef = useRef(0);
  const poolRef = useRef(null);

  // Consolidated refs
  const yoloRef = useRef({ failCount: 0, disabled: false, missCount: 0 });
  const temporalRef = useRef({ consecutiveDetections: 0, lastBoundingBox: null });
  const captureRef = useRef({ allPassingStart: null, detectionStart: null, manualCaptureShown: false });
  const feedbackRef = useRef({ lastKey: null, stableCount: 0 });
  const telemetryRef = useRef({ frameChain: [], orientation: [], orientationStart: null });

  const FEEDBACK_STABLE_FRAMES = 3;

  // Back-side fallback: if no detection but quality gates pass on the
  // guide box area, count consecutive quality-passing frames and accept
  const backQualityPassCountRef = useRef(0);
  const BACK_QUALITY_FRAMES_NEEDED = 5;

  // Best frame seen recently (assisted mode; see BEST_FRAME_WINDOW_MS).
  // One reusable full-resolution canvas; the video is copied into it
  // only when a frame beats the current best, so the common per-frame
  // cost is the score, which the gates already computed.
  const bestFrameRef = useRef({ canvas: null, score: -1, sharpness: null, luma: null, at: 0, considered: 0 });

  // Resolve capture strategy
  const strategyName = captureSettings?.captureStrategy || DEFAULT_STRATEGY;
  const strategy = CAPTURE_STRATEGIES[strategyName] || CAPTURE_STRATEGIES[DEFAULT_STRATEGY];

  // Stamped onto every telemetry event this hook emits — the hook only
  // runs in the Veratad in-browser capture, and the mode changes which
  // gates/timings the numbers were produced under.
  const telemetryBase = { provider: "veratad", mode: strategyName };

  // Merge settings: strategy defaults → captureSettings overrides
  const settings = {
    blurPolicy: "enabled",
    glarePolicy: "enabled",
    autoCaptureDelay: strategy.autoCaptureDelay,
    minimumDocumentArea: strategy.minimumDocumentArea,
    maximumDocumentArea: DOC_SIZE_MAX,
    temporalFrames: strategy.temporalFrames,
    temporalFramesBack: strategy.temporalFramesBack,
    temporalIOU: strategy.temporalIOU,
    blurThreshold: strategy.blurThreshold,
    glareAreaThreshold: strategy.glareAreaThreshold,
    glareAreaThresholdBack: null, // null = same ceiling as the front
    glareBackMetric: strategy.glareBackMetric || "near_white",
    centerThreshold: strategy.centerThreshold,
    minQualityGates: strategy.minQualityGates || 5,
    qualityAnalysis: strategy.qualityAnalysis || "full",
    captureStrategy: strategyName,
    darkRoomThreshold: DARK_ROOM_THRESHOLD,
    lumaStdMin: strategy.lumaStdMin ?? LUMA_STD_MIN,
    motionThreshold: strategy.motionThreshold ?? null, // null = gate off
    presenceEdgeMin: strategy.presenceEdgeMin ?? null, // null = heuristic off
    presenceRatioMin: strategy.presenceRatioMin ?? 1.2,
    ...captureSettings,
    lightingThresholds: {
      tooDarkThreshold: LUMA_MEAN_MIN,
      tooBrightThreshold: LUMA_MEAN_MAX,
      ...captureSettings?.lightingThresholds,
    },
  };

  const skipQuality = settings.qualityAnalysis === "skip";

  // The thresholds the gates are actually running with, in the shape
  // telemetry reports them (mirrors the face flow's metric names).
  const thresholds = {
    blur_min: settings.blurThreshold,
    blur_min_os: CAPTURE_OS,
    glare_max: settings.glareAreaThreshold,
    glare_max_back: settings.glareAreaThresholdBack ?? settings.glareAreaThreshold,
    glare_back_metric: settings.glareBackMetric,
    luma_min: settings.lightingThresholds.tooDarkThreshold,
    luma_max: settings.lightingThresholds.tooBrightThreshold,
    luma_std_min: settings.lumaStdMin,
    dark_room: settings.darkRoomThreshold,
    motion_delta_max: settings.motionThreshold,
    presence_edge_min: settings.presenceEdgeMin,
    presence_ratio_min: settings.presenceEdgeMin == null ? null : settings.presenceRatioMin,
  };
  const thresholdsRef = useRef(thresholds);
  thresholdsRef.current = thresholds;

  // Raw measurements from the most recent analysed frame, so a capture
  // can say what the frame actually scored next to the thresholds.
  const lastFrameRef = useRef(null);
  // Monotonic count of analysed frames. The screen records it when a side
  // starts and ignores gate state until a later frame has been analysed,
  // so the previous side's passing gates can never unlock the shutter.
  const frameSeqRef = useRef(0);
  const motionStateRef = useRef({ prev: null, prevMotion: null });

  // Load ONNX model (prefer preloaded cache from yoloModelCache)
  useEffect(() => {
    let cancelled = false;

    // Stupid / assisted mode: skip all model loading. Assisted still
    // needs the canvas pool for the per-frame quality analysis below.
    if (strategy.skipModels) {
      setModelsLoaded(true);
      if (!strategy.runFrameLoop) return;
      poolRef.current = createCanvasPool();
      return () => {
        if (poolRef.current) {
          poolRef.current.destroy();
          poolRef.current = null;
        }
        if (bestFrameRef.current.canvas) {
          bestFrameRef.current.canvas.width = 0;
          bestFrameRef.current.canvas.height = 0;
          bestFrameRef.current.canvas = null;
        }
      };
    }

    async function loadModel() {
      // Mirrors the face flow's model_load_started so the two funnels
      // share a load-start → ready/error stage; on the cache path
      // start and ready land back-to-back.
      trackEvent("id_doc_model_load_started", { ...telemetryBase }, { once: true });
      // Check if the model was already preloaded via yoloModelCache
      const cached = getPreloadedSession();
      if (cached) {
        console.log("[DocDetect] Using preloaded YOLO session from cache");
        sessionRef.current = cached;
        sessionFromCacheRef.current = true;
        trackEvent("id_doc_models_ready", { ...telemetryBase, source: "cache" }, { once: true });
        setModelsLoaded(true);
        return;
      }

      // Preload may still be in-flight — await it instead of starting a duplicate
      try {
        console.log("[DocDetect] Awaiting preload promise...");
        const session = await preloadYoloModel();
        if (!cancelled) {
          sessionRef.current = session;
          sessionFromCacheRef.current = true;
          trackEvent("id_doc_models_ready", { ...telemetryBase, source: "preload" }, { once: true });
          setModelsLoaded(true);
          console.log("[DocDetect] Using session from preload");
        }
      } catch (err) {
        console.warn("[DocDetect] Preload failed:", err.message);
        if (!cancelled) {
          trackEvent("id_doc_model_load_error", { ...telemetryBase, error: String(err) });
          setModelLoadError(err);
          setModelsLoaded(true);
        }
      }
    }

    // Create preprocess canvas with willReadFrequently hint
    preprocessCanvasRef.current = document.createElement("canvas");
    preprocessCtxRef.current = preprocessCanvasRef.current.getContext("2d", { willReadFrequently: true });

    // Create canvas pool
    poolRef.current = createCanvasPool();

    loadModel();

    return () => {
      cancelled = true;
      // Only release the session if we created it ourselves (not from cache)
      if (sessionRef.current && !sessionFromCacheRef.current) {
        sessionRef.current.release();
      }
      sessionRef.current = null;
      // Destroy canvas pool
      if (poolRef.current) {
        poolRef.current.destroy();
        poolRef.current = null;
      }
      if (bestFrameRef.current.canvas) {
        bestFrameRef.current.canvas.width = 0;
        bestFrameRef.current.canvas.height = 0;
        bestFrameRef.current.canvas = null;
      }
    };
  }, []);

  const setOnAutoCapture = useCallback((fn) => {
    onAutoCaptureRef.current = fn;
  }, []);

  // The capture screen can tell the loop where its guide frame sits in
  // video pixels ({x, y, width, height}); quality gates then measure
  // that region instead of the default centered 70% box. Read on every
  // frame so a rotation or resize is picked up without restarting the
  // loop. Returning null falls back to the default box.
  const guideBoxProviderRef = useRef(null);
  const setGuideBoxProvider = useCallback((fn) => {
    guideBoxProviderRef.current = fn;
  }, []);

  const getFrameChain = useCallback(() => [...telemetryRef.current.frameChain], []);

  // Detection loop
  useEffect(() => {
    // Stupid mode: no detection, just show manual capture
    if (strategy.skipModels && !strategy.runFrameLoop) {
      if (active) {
        setShowManualCapture(true);
        setFeedbackKey(side === "back" ? "positionBack" : "positionFront");
      }
      return;
    }

    console.log("[DocDetect] Detection loop effect fired — modelsLoaded:", modelsLoaded, "active:", active, "side:", side);
    if (!modelsLoaded || !active) {
      console.log("[DocDetect] Detection loop skipped (modelsLoaded:", modelsLoaded, "active:", active, ")");
      return;
    }

    const video = videoRef?.current?.video || videoRef?.current;
    if (!video) {
      console.log("[DocDetect] Detection loop skipped — no video element");
      return;
    }

    const pool = poolRef.current;
    if (!pool) {
      console.log("[DocDetect] Detection loop skipped — no canvas pool");
      return;
    }

    const isBack = side === "back";
    console.log(`[DocDetect] Detection loop starting (${isBack ? "back — relaxed" : "front — strict"} mode)`);
    trackFunnelOncePerSide("id_doc_detection_loop_started", { ...telemetryBase, side });
    let running = true;
    let loopCount = 0;
    captureRef.current.manualCaptureShown = false;
    captureRef.current.detectionStart = performance.now();

    // Reset state on loop start (important when switching front→back)
    setCaptured(false);
    temporalRef.current.consecutiveDetections = 0;
    temporalRef.current.lastBoundingBox = null;
    captureRef.current.allPassingStart = null;
    yoloRef.current.failCount = 0;
    yoloRef.current.disabled = false;
    yoloRef.current.missCount = 0;
    backQualityPassCountRef.current = 0;
    motionStateRef.current.prev = null;
    motionStateRef.current.prevMotion = null;
    resetBestFrame();
    // The per-frame gate state must not carry across sides: with it left
    // in place the back side reported "ready" before its first frame was
    // analysed (the front's passing gates were still in state), so the
    // shutter unlocked on stale data.
    setGatingState(defaultGatingState);
    setIsTooDark(false);
    lastFrameRef.current = null;
    feedbackRef.current.stableCount = 0;
    feedbackRef.current.lastKey = null;
    telemetryRef.current.frameChain = [];
    telemetryRef.current.orientation = [];
    telemetryRef.current.orientationStart = performance.now();
    setShowManualCapture(false);
    setFeedbackKey(isBack ? "positionBack" : "positionFront");

    // Orientation telemetry — collect device orientation samples during detection
    const orientationHandler = (e) => {
      if (telemetryRef.current.orientation.length >= 30) return;
      telemetryRef.current.orientation.push({
        t: Math.round(performance.now() - (telemetryRef.current.orientationStart || 0)),
        a: Math.round((e.alpha || 0) * 10) / 10,
        b: Math.round((e.beta || 0) * 10) / 10,
        g: Math.round((e.gamma || 0) * 10) / 10,
      });
    };
    window.addEventListener("deviceorientation", orientationHandler);

    const detect = async () => {
      if (!running) return;

      if (loopSuspendedRef.current) {
        timerRef.current = setTimeout(() => {
          if (running) rafRef.current = requestAnimationFrame(detect);
        }, 100);
        return;
      }

      if (video.readyState < 2) {
        rafRef.current = requestAnimationFrame(detect);
        return;
      }

      const now = performance.now();
      lastDetectRef.current = now;

      try {
        const frameH = video.videoHeight;
        const frameW = video.videoWidth;
        const frameArea = frameW * frameH;
        const newGating = { ...defaultGatingState };

        const frameBrightness = analyzeFrameBrightness(video, pool);
        const tooDark = frameBrightness < settings.darkRoomThreshold;

        let detectedBox = null;
        let detectedType = null;
        let detectedConfidence = 0;
        let rawDetected = false;

        // Run YOLO if model is available and not disabled
        let yoloRan = false;
        if (sessionRef.current && !yoloRef.current.disabled) {
          try {
            const inputTensor = preprocessFrame(
              video,
              preprocessCanvasRef.current,
              preprocessCtxRef.current
            );

            const feeds = {};
            const inputName = sessionRef.current.inputNames[0];
            feeds[inputName] = inputTensor;
            const results = await sessionRef.current.run(feeds);
            const outputName = sessionRef.current.outputNames[0];
            const output = results[outputName];

            const detections = postprocessYolo(output, frameW, frameH);
            yoloRan = true;
            yoloRef.current.failCount = 0;

            // First completed inference for this side — measures ONNX
            // session init + first-run cost (mirrors the face flow's
            // first_detection_completed)
            trackFunnelOncePerSide("id_doc_first_detection_completed", {
              ...telemetryBase,
              side,
              inference_ms: Math.round(performance.now() - now),
              detections: detections.length,
            });

            // Expose raw YOLO output for debug overlay
            setYoloDetail({
              source: "yolo",
              totalDetections: detections.length,
              all: detections.slice(0, 5).map(d => ({
                cls: d.className,
                conf: d.confidence,
                box: { x: Math.round(d.x), y: Math.round(d.y), w: Math.round(d.width), h: Math.round(d.height) },
              })),
              peaks: detections.peakScores || [],
              dims: `${output.dims.join("x")}`,
            });

            if (detections.length > 0) {
              const best = detections[0];
              const isBackClass = BACK_CLASS_IDS.has(best.classId);

              detectedBox = {
                x: best.x,
                y: best.y,
                width: best.width,
                height: best.height,
              };
              if (isBackClass) {
                detectedType = best.classId === 3 ? "id_card" : "drivers_license";
              } else {
                detectedType = best.className;
              }
              detectedConfidence = best.confidence;
              rawDetected = true;
              yoloRef.current.missCount = 0;
              trackFunnelOncePerSide("id_doc_first_detected", {
                ...telemetryBase,
                side,
                doc_type: detectedType,
                confidence: Math.round(detectedConfidence * 100) / 100,
              });
            } else {
              yoloRef.current.missCount++;
            }
          } catch (yoloErr) {
            yoloRef.current.failCount++;
            console.warn(`[DocDetect] YOLO inference failed (${yoloRef.current.failCount}/3):`, yoloErr.message);
            if (yoloRef.current.failCount >= 3) {
              console.warn("[DocDetect] YOLO disabled after 3 consecutive failures");
              yoloRef.current.disabled = true;
            }
            yoloRan = false;
          }
        }

        // When YOLO ran but found nothing, or YOLO unavailable: use guide box for quality feedback
        if (!detectedBox) {
          if (!yoloRan) {
            setYoloDetail({ source: "none", totalDetections: 0, all: [], dims: "n/a" });
          }
          let provided = null;
          try {
            provided = guideBoxProviderRef.current ? guideBoxProviderRef.current() : null;
          } catch {
            provided = null;
          }
          if (provided && provided.width > 0 && provided.height > 0) {
            detectedBox = provided;
          } else {
            const guideW = frameW * 0.7;
            const guideH = guideW / 1.586;
            detectedBox = {
              x: (frameW - guideW) / 2,
              y: (frameH - guideH) / 2,
              width: guideW,
              height: guideH,
            };
          }
        }

        // ── Temporal consistency ──
        const baseFrames = isBack ? settings.temporalFramesBack : settings.temporalFrames;
        const framesNeeded = baseFrames;

        if (rawDetected && detectedBox) {
          const prevBox = temporalRef.current.lastBoundingBox;
          if (prevBox) {
            const overlap = iou(detectedBox, prevBox);
            if (overlap >= settings.temporalIOU) {
              temporalRef.current.consecutiveDetections++;
            } else {
              temporalRef.current.consecutiveDetections = 1;
            }
          } else {
            temporalRef.current.consecutiveDetections = 1;
          }
          temporalRef.current.lastBoundingBox = detectedBox;

          const temporallyConfirmed =
            temporalRef.current.consecutiveDetections >= framesNeeded;
          newGating.documentPresent = temporallyConfirmed;
          newGating.classified = temporallyConfirmed && detectedType != null;
        } else {
          temporalRef.current.consecutiveDetections = Math.max(0, temporalRef.current.consecutiveDetections - 1);
          temporalRef.current.lastBoundingBox = null;
        }

        if (detectedBox) {
          // Gate: Document size
          const docArea = detectedBox.width * detectedBox.height;
          const areaRatio = docArea / frameArea;
          areaRatioRef.current = areaRatio;
          newGating.size =
            areaRatio >= settings.minimumDocumentArea &&
            areaRatio <= settings.maximumDocumentArea;

          // Gate: Centering
          const docCenterX = detectedBox.x + detectedBox.width / 2;
          const docCenterY = detectedBox.y + detectedBox.height / 2;
          const xOff = Math.abs(docCenterX - frameW / 2) / frameW;
          const yOff = Math.abs(docCenterY - frameH / 2) / frameH;
          newGating.centering =
            xOff <= settings.centerThreshold && yOff <= settings.centerThreshold;

          let blurVariance = null;
          let lumaMean = null;
          let lumaStd = null;
          let glareRatio = null;
          let glareClipped = null;
          let glareUsed = null;
          let glareMetric = null;
          let motion = null;
          let motionDelta = null;
          let presence = null;
          if (skipQuality) {
            // Snap strategy: auto-pass blur/glare/lighting
            newGating.blur = true;
            newGating.glare = true;
            newGating.lighting = true;
          } else {
            // Gate: Blur
            if (settings.blurPolicy === "disabled") {
              newGating.blur = true;
            } else {
              blurVariance = analyzeBlur(video, detectedBox, pool);
              newGating.blur = blurVariance > settings.blurThreshold;
            }

            // Gate: Glare
            if (settings.glarePolicy === "disabled") {
              newGating.glare = true;
            } else {
              const gl = analyzeGlare(video, detectedBox, pool);
              glareRatio = gl.ratio;
              glareClipped = gl.clipped;
              // The value the gate judged: clipped pixels on the back when
              // the strategy says so, near-white pixels everywhere else.
              const backClipped = isBack && settings.glareBackMetric === "clipped";
              glareUsed = backClipped ? gl.clipped : gl.ratio;
              glareMetric = backClipped ? "clipped" : "near_white";
              const glareMax = isBack && settings.glareAreaThresholdBack != null
                ? settings.glareAreaThresholdBack
                : settings.glareAreaThreshold;
              newGating.glare = glareUsed < glareMax;
            }

            // Gate: Lighting
            const { mean, stdDev } = analyzeLighting(video, detectedBox, pool);
            lumaMean = mean;
            lumaStd = stdDev;
            newGating.lighting =
              mean >= settings.lightingThresholds.tooDarkThreshold &&
              mean <= settings.lightingThresholds.tooBrightThreshold &&
              stdDev >= settings.lumaStdMin;

            // Gate: Steady (only when the strategy sets a motion threshold).
            // Passes when the motion reading has settled: its frame-to-frame
            // change is within the threshold. An unmeasurable frame (first
            // two frames, box resized) never blocks.
            if (settings.motionThreshold != null) {
              const m = analyzeMotion(video, detectedBox, pool, motionStateRef.current);
              motion = m?.motion ?? null;
              motionDelta = m?.delta ?? null;
              newGating.steady = motionDelta == null || motionDelta <= settings.motionThreshold;
            }

            // Presence heuristic (assisted mode, for auto-capture only).
            // Unmeasurable = not likely, so auto never fires blind.
            if (settings.presenceEdgeMin != null) {
              const p = analyzePresence(video, detectedBox, pool);
              presence = p;
              newGating.documentLikely =
                !!p && p.edge_in >= settings.presenceEdgeMin && p.edge_in >= settings.presenceRatioMin * p.edge_out;
            }
          }

          lastFrameRef.current = {
            blur_var: blurVariance == null ? null : Math.round(blurVariance),
            glare_ratio: glareRatio == null ? null : Math.round(glareRatio * 1000) / 1000,
            glare_clipped: glareClipped == null ? null : Math.round(glareClipped * 1000) / 1000,
            glare_used: glareUsed == null ? null : Math.round(glareUsed * 1000) / 1000,
            glare_metric: glareMetric,
            luma_mean: lumaMean == null ? null : Math.round(lumaMean),
            luma_std: lumaStd == null ? null : Math.round(lumaStd),
            motion: motion == null ? null : Math.round(motion * 10) / 10,
            motion_delta: motionDelta == null ? null : Math.round(motionDelta * 10) / 10,
            edge_in: presence == null ? null : Math.round(presence.edge_in * 10) / 10,
            edge_out: presence == null ? null : Math.round(presence.edge_out * 10) / 10,
            frame_brightness: Math.round(frameBrightness),
            analysed_at: Math.round(now),
          };

          // Best frame (assisted mode): every frame that clears the quality
          // gates is a valid capture; remember the best one for the shutter.
          // Sharpness dominates; a frame outside the luma band is discounted
          // rather than excluded, so a dim-but-sharp frame still beats a
          // bright-but-smeared one.
          if (
            strategy.runFrameLoop &&
            !tooDark &&
            newGating.blur &&
            newGating.glare &&
            newGating.lighting &&
            newGating.steady &&
            blurVariance != null
          ) {
            const b = bestFrameRef.current;
            const lumaOk = lumaMean != null && lumaMean >= BEST_FRAME_LUMA_MIN && lumaMean <= BEST_FRAME_LUMA_MAX;
            const score = lumaOk ? blurVariance : blurVariance * 0.5;
            const stale = b.at > 0 && now - b.at > BEST_FRAME_WINDOW_MS;
            b.considered += 1;
            if (score > b.score || stale) {
              try {
                if (!b.canvas) b.canvas = document.createElement("canvas");
                if (b.canvas.width !== frameW) b.canvas.width = frameW;
                if (b.canvas.height !== frameH) b.canvas.height = frameH;
                b.canvas.getContext("2d").drawImage(video, 0, 0, frameW, frameH);
                b.score = score;
                b.sharpness = blurVariance;
                b.luma = lumaMean;
                b.at = now;
              } catch (e) {
                // A failed copy leaves the previous best in place.
              }
            }
          }
        }

        // ── Back-side quality-gate fallback ──
        if (isBack && !newGating.documentPresent) {
          const qualityOk = newGating.blur && newGating.glare && newGating.lighting && newGating.size;
          if (qualityOk) {
            backQualityPassCountRef.current++;
            if (backQualityPassCountRef.current >= BACK_QUALITY_FRAMES_NEEDED) {
              newGating.documentPresent = true;
              newGating.classified = true;
              newGating.centering = true;
            }
          } else {
            backQualityPassCountRef.current = 0;
          }
        }

        if (loopCount % 10 === 0) {
          console.log("[DocDetect] Gates:", {
            documentPresent: `${newGating.documentPresent} (streak: ${temporalRef.current.consecutiveDetections}/${framesNeeded}, side: ${side}, strategy: ${settings.captureStrategy})`,
            classified: `${newGating.classified} (${detectedType || "none"} @ ${(detectedConfidence * 100).toFixed(1)}%)`,
            yoloMisses: yoloRef.current.missCount,
            size: newGating.size,
            centering: newGating.centering,
            blur: newGating.blur,
            glare: newGating.glare,
            lighting: newGating.lighting,
          });
        }

        // Record frame chain entry for capture integrity
        telemetryRef.current.frameChain.push({
          t: Math.round(now),
          d: rawDetected ? 1 : 0,
          g: Object.values(newGating).filter(Boolean).length,
        });
        if (telemetryRef.current.frameChain.length > 200) telemetryRef.current.frameChain.shift();

        // YOLO gates (documentPresent + classified) are mandatory.
        // Quality gates (size, centering, blur, glare, lighting) use a threshold.
        const yoloOk = newGating.documentPresent && newGating.classified;
        const qualityGates = [newGating.size, newGating.centering, newGating.blur, newGating.glare, newGating.lighting];
        const qualityPassing = qualityGates.filter(Boolean).length;
        const allPassing = yoloOk && qualityPassing >= settings.minQualityGates;

        frameSeqRef.current += 1;
        setGatingState(newGating);
        setIsTooDark(tooDark);

        // Debounce feedback
        const newKey = getFeedbackKey(newGating, tooDark, areaRatioRef.current, settings.minimumDocumentArea, settings.maximumDocumentArea);
        if (newKey === feedbackRef.current.lastKey) {
          feedbackRef.current.stableCount++;
        } else {
          feedbackRef.current.lastKey = newKey;
          feedbackRef.current.stableCount = 1;
        }
        if (newKey === "holdStill" || feedbackRef.current.stableCount >= FEEDBACK_STABLE_FRAMES) {
          setFeedbackKey(newKey);
        }

        setIsAllPassing(allPassing);
        setDocumentType(detectedType);
        setConfidence(detectedConfidence);
        setBoundingBox(detectedBox);

        // Auto-capture state machine
        if (allPassing) {
          if (!captureRef.current.allPassingStart) {
            captureRef.current.allPassingStart = performance.now();
            trackFunnelOncePerSide("id_doc_gates_first_passed", { ...telemetryBase, side });
            console.log("[DocDetect] All gates PASSING — starting timer");
          }
          const elapsed = performance.now() - captureRef.current.allPassingStart;
          if (elapsed >= settings.autoCaptureDelay) {
            console.log("[DocDetect] Auto-capture triggered!");
            trackEvent("id_doc_auto_capture_triggered", { ...telemetryBase, side, doc_type: detectedType });
            if (onAutoCaptureRef.current) {
              running = false;
              setCaptured(true);
              onAutoCaptureRef.current();
              return;
            }
          }
        } else {
          if (captureRef.current.allPassingStart) {
            console.log("[DocDetect] Gates broke — timer reset");
          }
          captureRef.current.allPassingStart = null;
        }

        // Show manual capture button after timeout
        if (!captureRef.current.manualCaptureShown && captureRef.current.detectionStart) {
          const elapsed = performance.now() - captureRef.current.detectionStart;
          if (elapsed >= MANUAL_CAPTURE_TIMEOUT) {
            captureRef.current.manualCaptureShown = true;
            setShowManualCapture(true);
            trackFunnelOncePerSide("id_doc_manual_capture_shown", { ...telemetryBase, side });
            console.log("[DocDetect] Manual capture fallback enabled (10s timeout)");
          }
        }
      } catch (err) {
        console.error("[DocDetect] Detection error:", err);
      }

      loopCount++;

      // Backpressure: wait for remaining throttle time before scheduling next frame
      if (running) {
        const elapsed = performance.now() - now;
        const remaining = Math.max(0, THROTTLE_MS - elapsed);
        if (remaining > 0) {
          timerRef.current = setTimeout(() => {
            if (running) rafRef.current = requestAnimationFrame(detect);
          }, remaining);
        } else {
          rafRef.current = requestAnimationFrame(detect);
        }
      }
    };

    rafRef.current = requestAnimationFrame(detect);

    return () => {
      console.log("[DocDetect] Detection loop CLEANUP (side:", side, "active:", active, ")");
      running = false;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      window.removeEventListener("deviceorientation", orientationHandler);
    };
  }, [modelsLoaded, active, videoRef, side]);

  const getOrientationSamples = useCallback(() => [...telemetryRef.current.orientation], []);

  /** Idle the frame loop (true) or let it analyse again (false). State is kept either way. */
  const setFrameLoopSuspended = useCallback((suspended) => {
    loopSuspendedRef.current = !!suspended;
  }, []);

  const resetBestFrame = useCallback(() => {
    const b = bestFrameRef.current;
    b.score = -1;
    b.sharpness = null;
    b.luma = null;
    b.at = 0;
    b.considered = 0;
  }, []);

  /** Raw measurements of the last analysed frame, with their age. */
  const getLastFrameMetrics = useCallback(() => {
    const m = lastFrameRef.current;
    if (!m) return null;
    const { analysed_at, ...rest } = m;
    return { ...rest, frame_age_ms: Math.round(performance.now() - analysed_at) };
  }, []);

  /** The thresholds the gates are currently running with. */
  const getThresholds = useCallback(() => ({ ...thresholdsRef.current }), []);

  /** The best gate-passing frame from the last BEST_FRAME_WINDOW_MS, or null. */
  const getBestFrame = useCallback(() => {
    const b = bestFrameRef.current;
    if (!b.canvas || b.score < 0) return null;
    const age = Math.round(performance.now() - b.at);
    if (age > BEST_FRAME_WINDOW_MS) return null;
    return {
      canvas: b.canvas,
      sharpness: b.sharpness,
      luma: b.luma,
      age_ms: age,
      considered: b.considered,
    };
  }, []);

  /**
   * Sharpness (Laplacian variance) of the live video inside the guide
   * box right now, measured the same way the gates measure candidates so
   * the shutter's best-vs-current comparison is apples to apples.
   */
  const measureCurrentSharpness = useCallback(() => {
    try {
      const video = videoRef?.current?.video || videoRef?.current;
      const pool = poolRef.current;
      if (!video || !pool || !video.videoWidth) return null;
      let box = null;
      try {
        box = guideBoxProviderRef.current ? guideBoxProviderRef.current() : null;
      } catch {
        box = null;
      }
      if (!box) {
        const guideW = video.videoWidth * 0.7;
        const guideH = guideW / 1.586;
        box = { x: (video.videoWidth - guideW) / 2, y: (video.videoHeight - guideH) / 2, width: guideW, height: guideH };
      }
      return analyzeBlur(video, box, pool);
    } catch {
      return null;
    }
  }, [videoRef]);

  return {
    modelsLoaded,
    modelLoadError,
    gatingState,
    feedbackKey,
    isAllPassing,
    isTooDark,
    documentType,
    confidence,
    boundingBox,
    showManualCapture,
    setOnAutoCapture,
    setGuideBoxProvider,
    getBestFrame,
    measureCurrentSharpness,
    getLastFrameMetrics,
    setFrameLoopSuspended,
    getFrameSeq: () => frameSeqRef.current,
    getThresholds,
    thresholds,
    getFrameChain,
    getOrientationSamples,
    areaRatio: areaRatioRef.current,
    captured,
    yoloDetail,
    captureStrategy: settings.captureStrategy,
    yoloMissCount: yoloRef.current.missCount,
  };
}
